export type IndividuumRow = {
  id: string;
  taxon: string;
  condicio: string;
  nomen: string | null;
  nomen_aequatum: string | null;
  descriptio: string | null;
  creatum: number;
  mutatum: number;
};

export type FetchLike = typeof fetch;

export type UiDeps = {
  fetch: FetchLike;
  baseUrl: string;
  token: string;
};

export type UiResult<T> = {
  ok: boolean;
  status: number;
  data: T | null;
  error: string | null;
};

export function makeUi(deps: UiDeps) {
  async function call<T>(method: string, path: string, body?: unknown): Promise<UiResult<T>> {
    let res: Response;
    try {
      res = await deps.fetch(`${deps.baseUrl.replace(/\/$/, "")}${path}`, {
        method,
        headers: {
          ...(deps.token ? { authorization: `Bearer ${deps.token}` } : {}),
          ...(body !== undefined ? { "content-type": "application/json" } : {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch (err) {
      return { ok: false, status: 0, data: null, error: String(err) };
    }
    const text = await res.text();
    let parsed: unknown = null;
    try { parsed = text ? JSON.parse(text) : null; } catch { parsed = text; }
    if (!res.ok) {
      const msg =
        parsed && typeof parsed === "object" && "error" in parsed
          ? JSON.stringify((parsed as { error: unknown }).error)
          : String(parsed ?? res.statusText);
      return { ok: false, status: res.status, data: null, error: msg };
    }
    return { ok: true, status: res.status, data: parsed as T, error: null };
  }

  return {
    ping() {
      return call<{ service: string; version: string }>("GET", ``);
    },
    browse(opts: { taxon?: string; condicio?: string; limit?: number } = {}) {
      const p = new URLSearchParams();
      if (opts.taxon) p.set("taxon", opts.taxon);
      if (opts.condicio) p.set("condicio", opts.condicio);
      if (opts.limit != null) p.set("limit", String(opts.limit));
      p.set("$count", "true");
      const qs = p.toString();
      return call<{ value: IndividuumRow[]; "@odata.count"?: number }>("GET", `/Individua${qs ? `?${qs}` : ""}`);
    },
    async search(query: string, taxon?: string): Promise<UiResult<{ value: IndividuumRow[] }>> {
      const r = await this.browse({ taxon, limit: 1000 });
      if (!r.ok || !r.data) return { ok: r.ok, status: r.status, data: null, error: r.error };
      const q = query.toLowerCase();
      const filtered = r.data.value.filter(row =>
        (row.nomen ?? "").toLowerCase().includes(q) ||
        (row.nomen_aequatum ?? "").toLowerCase().includes(q)
      );
      return { ok: true, status: 200, data: { value: filtered }, error: null };
    },
    read(id: string) {
      return call<IndividuumRow>("GET", `/Individua/${encodeURIComponent(id)}`);
    },
    create(body: { nomen?: string; taxon?: string; descriptio?: string }) {
      return call<IndividuumRow>("POST", `/Individua`, body);
    },
    remove(id: string) {
      return call<IndividuumRow>("POST", `/Individua/${encodeURIComponent(id)}/tollere`, {});
    },
  };
}

export type Ui = ReturnType<typeof makeUi>;

// — Settings (localStorage) —

export type Settings = { baseUrl: string; token: string };
const LS_KEY = "thw8-individuum-ui:settings";

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw) as Settings;
  } catch { /* ignore */ }
  return { baseUrl: "http://localhost:8787/v0.1.0", token: "" };
}

export function saveSettings(s: Settings): void {
  localStorage.setItem(LS_KEY, JSON.stringify(s));
}

// — Browser attach —

if (typeof document !== "undefined") {
  let settings = loadSettings();
  let ui = makeUi({ fetch: globalThis.fetch.bind(globalThis), baseUrl: settings.baseUrl, token: settings.token });

  const $ = <T extends HTMLElement>(sel: string): T => {
    const el = document.querySelector(sel);
    if (!el) throw new Error(`element not found: ${sel}`);
    return el as T;
  };

  // theme toggle
  const themeBtn = $("#theme-toggle");
  function applyTheme(t: string) {
    document.documentElement.dataset.theme = t;
    themeBtn.textContent = t === "dark" ? "☀️" : "🌙";
    localStorage.setItem("thw8-theme", t);
  }
  applyTheme(document.documentElement.dataset.theme ?? "light");
  themeBtn.addEventListener("click", () => {
    applyTheme(document.documentElement.dataset.theme === "dark" ? "light" : "dark");
  });

  const setStatus = (msg: string, kind: "info" | "error" = "info") => {
    const s = $("#status");
    s.textContent = msg;
    s.classList.toggle("error", kind === "error");
  };
  const fmtDate = (ms: number | null | undefined) =>
    ms ? new Date(ms).toISOString().slice(0, 19).replace("T", " ") : "—";

  function rebuildUi() {
    ui = makeUi({ fetch: globalThis.fetch.bind(globalThis), baseUrl: settings.baseUrl, token: settings.token });
  }

  function renderRows(rows: IndividuumRow[]) {
    const tbody = $("#results-table tbody");
    tbody.innerHTML = "";
    if (!rows.length) {
      tbody.innerHTML = `<tr><td colspan="7" class="muted">— üres találat —</td></tr>`;
      return;
    }
    for (const row of rows) {
      const tr = document.createElement("tr");
      tr.dataset.id = row.id;
      const sublatum = row.condicio === "sublatum";
      tr.innerHTML = `
        <td class="id">${row.id}</td>
        <td>${row.taxon}</td>
        <td>${row.nomen ?? '<span class="muted">(noname)</span>'}</td>
        <td class="muted">${row.nomen_aequatum ?? "—"}</td>
        <td class="${sublatum ? "sublatum" : "muted"}">${row.condicio}</td>
        <td class="muted">${fmtDate(row.creatum)}</td>
        <td><button class="row-open" type="button">↗</button></td>`;
      tbody.appendChild(tr);
    }
  }

  function showDetail(row: IndividuumRow) {
    const d = $<HTMLElement>("#detail");
    d.hidden = false;
    $("#detail-content").textContent = JSON.stringify(row, null, 2);
    d.dataset.id = row.id;
    d.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }
  const hideDetail = () => { $<HTMLElement>("#detail").hidden = true; };

  async function doBrowse() {
    setStatus("listázás…");
    const r = await ui.browse({
      taxon: $<HTMLInputElement>("#filter-taxon").value || undefined,
      condicio: $<HTMLInputElement>("#filter-condicio").value || undefined,
      limit: 200,
    });
    if (!r.ok || !r.data) return setStatus(r.error ?? "hiba", "error");
    renderRows(r.data.value);
    setStatus(`${r.data.value.length} sor (összes: ${r.data["@odata.count"] ?? "?"})`);
  }

  async function doSearch() {
    const query = $<HTMLInputElement>("#search-query").value.trim();
    if (!query) return doBrowse();
    setStatus("keresés…");
    const r = await ui.search(query, $<HTMLInputElement>("#filter-taxon").value || undefined);
    if (!r.ok || !r.data) return setStatus(r.error ?? "hiba", "error");
    renderRows(r.data.value);
    setStatus(`${r.data.value.length} találat`);
  }

  async function doCreate(ev: SubmitEvent) {
    ev.preventDefault();
    const form = ev.target as HTMLFormElement;
    const fd = new FormData(form);
    const body: Record<string, string> = {};
    for (const k of ["nomen", "taxon", "descriptio"] as const) {
      const v = String(fd.get(k) ?? "").trim();
      if (v) body[k] = v;
    }
    setStatus("létrehozás…");
    const r = await ui.create(body);
    if (!r.ok || !r.data) return setStatus(r.error ?? "hiba", "error");
    setStatus(`létrehozva: ${r.data.id}`);
    form.reset();
    await doBrowse();
    showDetail(r.data);
  }

  async function doDelete() {
    const id = $<HTMLElement>("#detail").dataset.id;
    if (!id || !confirm(`Törlöd: ${id}?`)) return;
    const r = await ui.remove(id);
    if (!r.ok) return setStatus(r.error ?? "hiba", "error");
    setStatus(`törölve: ${id}`);
    hideDetail();
    await doBrowse();
  }

  async function openRow(id: string) {
    setStatus("betöltés…");
    const r = await ui.read(id);
    if (!r.ok || !r.data) return setStatus(r.error ?? "hiba", "error");
    showDetail(r.data);
    setStatus("");
  }

  // Settings dialog
  const dlg = $<HTMLDialogElement>("#settings-dialog");
  $("#settings-btn").addEventListener("click", () => {
    $<HTMLInputElement>("#settings-url").value = settings.baseUrl;
    $<HTMLInputElement>("#settings-token").value = settings.token;
    dlg.showModal();
  });
  $("#settings-cancel").addEventListener("click", () => dlg.close());
  dlg.addEventListener("close", async () => {
    if (dlg.returnValue !== "save") return;
    settings = {
      baseUrl: $<HTMLInputElement>("#settings-url").value.trim(),
      token: $<HTMLInputElement>("#settings-token").value,
    };
    saveSettings(settings);
    rebuildUi();
    const p = await ui.ping();
    setStatus(p.ok ? "backend OK" : `backend nem elérhető: ${p.error}`, p.ok ? "info" : "error");
    if (p.ok) await doBrowse();
  });

  $("#search-btn").addEventListener("click", () => void doSearch());
  $("#browse-btn").addEventListener("click", () => void doBrowse());
  $("#search-query").addEventListener("keydown", (ev) => {
    if ((ev as KeyboardEvent).key === "Enter") void doSearch();
  });
  $("#create-form").addEventListener("submit", (ev) => void doCreate(ev as SubmitEvent));
  $("#detail-close").addEventListener("click", hideDetail);
  $("#detail-delete").addEventListener("click", () => void doDelete());
  $("#results-table").addEventListener("click", (ev) => {
    const btn = (ev.target as HTMLElement).closest(".row-open");
    if (btn) {
      const tr = btn.closest("tr[data-id]") as HTMLTableRowElement | null;
      if (tr?.dataset.id) void openRow(tr.dataset.id);
    }
  });

  if (settings.token && settings.baseUrl) void doBrowse();
  else setStatus("⚙️ töltsd ki a beállításokat (jobb fent)");
}
